import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ECUADOR } from "../config/ecuador";
import {
  crearPedidoLanding,
  guardarCarritoAbandonado,
  marcarCarritoConvertido,
  retirarCarritoAbandonado,
} from "../config/firebase";
import {
  buildAbandonedCartPayload,
  clearAbandonedCartVisitorId,
  getAbandonedCartCreatedAt,
  getAbandonedCartVisitorId,
} from "../utils/abandonedCart";
import { buildMetaConsentFields } from "../utils/metaConsent";
import {
  collectCampaignAttribution,
  primeCampaignAttribution,
} from "../utils/campaignAttribution";
import { loadCampaignLanding } from "../services/campaignLandings";
import "./CampaignLanding.css";
import "./CampaignLandingTheme.css";

const PIXEL_ID = "1973192360282473";
const STORE_WHATSAPP = "593994683079";

const DEFAULT_FORM_TEXT = {
  offerTitle: "Elige tu oferta",
  shippingTitle: "Datos para la entrega",
  summaryTitle: "Resumen de tu pedido",
  nameLabel: "Nombre completo",
  phoneLabel: "Número de WhatsApp",
  provinceLabel: "Provincia",
  cityLabel: "Ciudad o cantón",
  addressLabel: "Dirección exacta",
  referenceLabel: "Referencia obligatoria",
  observationsLabel: "Observaciones (opcional)",
  namePlaceholder: "Nombres y apellidos",
  phonePlaceholder: "0991234567",
  addressPlaceholder: "Calle principal, secundaria, número de casa, barrio o sector",
  referencePlaceholder: "Color de la vivienda y un punto cercano",
  observationsPlaceholder: "Horario de entrega u otra indicación importante",
  consentText: "Autorizo a ROMI STORE EC a contactarme por WhatsApp para verificar los datos y coordinar la entrega de este pedido.",
};

const newRequestId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return `order-${window.crypto.randomUUID()}`;
  return `order-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const sendPixel = (name, data = {}, eventId = "") => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", name, data, eventId ? { eventID: eventId } : undefined);
  }
};

function useMetaPixel(landing) {
  useEffect(() => {
    if (!landing) return;
    if (!window.fbq) {
      const fbq = function () { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
      fbq.push = fbq; fbq.loaded = true; fbq.version = "2.0"; fbq.queue = []; window.fbq = fbq; window._fbq = fbq;
      const script = document.createElement("script"); script.async = true; script.src = "https://connect.facebook.net/en_US/fbevents.js"; document.head.appendChild(script);
      fbq("init", PIXEL_ID);
    }
    sendPixel("PageView");
    sendPixel("ViewContent", { content_name: landing.product, content_ids: [landing.productId], content_type: "product", value: landing.offers[0].price, currency: "USD" });
  }, [landing]);
}

function useLandingMeta(landing, slug) {
  useEffect(() => {
    if (!landing) return;
    const values = {
      description: `${landing.subtitle}. Envío gratis a todo Ecuador y pago contra entrega.`,
      "og:title": `${landing.product} | ROMI STORE EC`,
      "og:description": `${landing.subtitle}. Compra segura y paga al recibir.`,
      "og:image": new URL(landing.images[0], window.location.origin).href,
      "og:url": `${window.location.origin}/l/${slug}`,
      "og:type": "product",
    };
    document.title = `${landing.product} | ROMI STORE EC`;
    Object.entries(values).forEach(([name, content]) => {
      const attr = name.startsWith("og:") ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    });
  }, [landing, slug]);
}

function CampaignEditorBlock({ block, config, landing, goToForm, renderOrder }) {
  const style = {
    background: block.background || "transparent",
    color: block.color || "inherit",
    fontFamily: block.fontFamily || "Poppins, Arial, sans-serif",
    fontSize: block.fontSize,
    fontWeight: block.fontWeight,
    fontStyle: block.fontStyle,
    textAlign: block.textAlign,
    "--campaign-heading-family": block.headingFontFamily || "Poppins, Arial, sans-serif",
    "--campaign-heading-size": `${block.headingFontSize || 30}px`,
    "--campaign-heading-weight": block.headingFontWeight || 800,
    "--campaign-heading-style": block.headingFontStyle || "normal",
    "--campaign-heading-align": block.headingAlign || "center",
  };
  if (block.type === "marquee") {
    const items = block.items?.length ? block.items : ["ENVÍO GRATIS A TODO ECUADOR", "PAGO CONTRA ENTREGA", "COMPRA SEGURA"];
    return <section className="campaign-editor-block campaign-marquee" style={style}><div>{[...items, ...items].map((item, index) => <b key={`${item}-${index}`}>{item}</b>)}</div></section>;
  }
  if (block.type === "html") return <section className="campaign-editor-block campaign-editor-html" style={style} dangerouslySetInnerHTML={{ __html: block.html || "" }} />;
  if (block.type === "image") return <section className="campaign-editor-block campaign-editor-image" style={style}>{block.url && <img src={block.url} alt={block.text || landing.product} />}</section>;
  if (block.type === "carousel") return <section className="campaign-editor-block campaign-editor-carousel" style={style}>{block.images?.map((url, index) => <img src={url} alt={`${landing.product} ${index + 1}`} key={`${url}-${index}`} />)}</section>;
  if (block.type === "button") {
    const buttonStyle = { background: block.buttonBackground || config.buttonBackground, color: block.buttonColor || config.buttonColor, borderRadius: block.buttonRadius ?? 40, fontFamily: block.buttonFontFamily || "Poppins, Arial, sans-serif", fontSize: block.buttonFontSize || 16, fontWeight: block.buttonFontWeight || 800, fontStyle: block.buttonFontStyle || "normal" };
    return <section className="campaign-editor-block campaign-editor-button" style={style}><button type="button" className={`campaign-effect-${block.buttonEffect || "none"}`} style={buttonStyle} onClick={goToForm}>{block.text || "QUIERO COMPRAR"}</button></section>;
  }
  if (block.type === "benefits") return <section className="campaign-editor-block campaign-editor-benefits" style={style}><h2>{block.text}</h2>{block.items?.map((item) => <p key={item}>✓ {item}</p>)}</section>;
  if (block.type === "form") return <section className="campaign-editor-block campaign-editor-form" style={style}>{renderOrder(block)}</section>;
  return null;
}

export default function CampaignLanding() {
  const { slug } = useParams();
  const [landingState, setLandingState] = useState(() => ({ slug, landing: null, loading: true }));
  const landing = landingState.slug === slug ? landingState.landing : null;
  const loading = landingState.slug === slug ? landingState.loading : true;
  const [selectedOfferQty, setSelectedOfferQty] = useState(null);
  const offer = landing?.offers.find((item) => item.qty === selectedOfferQty)
    || landing?.offers.find((item) => item.featured)
    || landing?.offers[0];
  useMetaPixel(landing); useLandingMeta(landing, slug);
  const [province, setProvince] = useState(""); const [sending, setSending] = useState(false); const [orderId, setOrderId] = useState(""); const checkoutTracked = useRef(false); const submitting = useRef(false); const pendingOrderAttempt = useRef(null);
  const formRef = useRef(null); const abandonedTimer = useRef(null); const abandonedWrite = useRef(Promise.resolve()); const abandonedVisitorId = useRef(""); const lastAbandonedSignature = useRef("");
  const cities = useMemo(() => ECUADOR.find((item) => item.provincia === province)?.ciudades || [], [province]);
  useEffect(() => {
    primeCampaignAttribution();
  }, [slug]);
  useEffect(() => {
    const controller = new AbortController();
    setLandingState({ slug, landing: null, loading: true });
    setSelectedOfferQty(null);
    setProvince("");
    setOrderId("");
    checkoutTracked.current = false;
    submitting.current = false;
    pendingOrderAttempt.current = null;
    loadCampaignLanding(slug, { signal: controller.signal }).then((loaded) => {
      if (!controller.signal.aborted) setLandingState({ slug, landing: loaded, loading: false });
    });
    return () => controller.abort();
  }, [slug]);
  useEffect(() => () => { if (abandonedTimer.current) window.clearTimeout(abandonedTimer.current); }, []);
  if (loading) return <main className="campaign-not-found campaign-loading"><span>Cargando oferta...</span></main>;
  if (!landing) return <main className="campaign-not-found"><h1>Landing no encontrada</h1><a href="/">Volver a ROMI STORE</a></main>;
  const theme = landing.theme || { page: "#020712", ink: "#13aec3", accent: "#00d3fe", accentInk: "#071018", border: "#315df0", form: "#dee3ed", formInk: "#020712" };
  const pageStyle = { "--campaign-page": theme.page, "--campaign-ink": theme.ink, "--campaign-accent": theme.accent, "--campaign-accent-ink": theme.accentInk, "--campaign-border": theme.border, "--campaign-form": theme.form, "--campaign-form-ink": theme.formInk };
  const goToForm = () => { sendPixel("AddToCart", { content_ids: [landing.productId], content_type: "product", value: offer.price, currency: "USD" }); document.getElementById("pedido")?.scrollIntoView({ behavior: "smooth" }); };
  const startCheckout = () => { if (checkoutTracked.current) return; checkoutTracked.current = true; sendPixel("InitiateCheckout", { content_ids: [landing.productId], content_type: "product", value: offer.price, currency: "USD", num_items: offer.qty }); };
  const queueAbandonedOperation = (operation) => {
    const queued = abandonedWrite.current.catch(() => {}).then(operation);
    abandonedWrite.current = queued;
    return queued;
  };
  const abandonedPayload = (formElement) => {
    if (!formElement?.elements?.seguimientoWhatsapp?.checked) return null;
    const form = new FormData(formElement);
    const visitorId = abandonedVisitorId.current || getAbandonedCartVisitorId(slug);
    abandonedVisitorId.current = visitorId;
    return buildAbandonedCartPayload({
      visitorId,
      landingSlug: slug,
      productId: landing.productId,
      productName: landing.product,
      offerQty: offer.qty,
      offerPrice: offer.price,
      name: form.get("nombre"),
      phone: form.get("telefono"),
      createdAt: getAbandonedCartCreatedAt(slug),
    });
  };
  const scheduleAbandonedCart = () => {
    if (abandonedTimer.current) window.clearTimeout(abandonedTimer.current);
    const formElement = formRef.current;
    const consentGranted = Boolean(formElement?.elements?.seguimientoWhatsapp?.checked);

    if (!consentGranted) {
      const visitorId = abandonedVisitorId.current;
      if (visitorId) {
        const withdrawal = {
          landingSlug: slug,
          visitorId,
          productoId: landing.productId,
          creadoEn: getAbandonedCartCreatedAt(slug),
        };
        const withdrawalWrite = queueAbandonedOperation(() => retirarCarritoAbandonado(withdrawal));
        // Rotar inmediatamente evita que un consentimiento nuevo reutilice
        // un intento que ya está camino a quedar retirado.
        clearAbandonedCartVisitorId(slug);
        abandonedVisitorId.current = "";
        lastAbandonedSignature.current = "";
        withdrawalWrite
          .catch((error) => console.warn("No se pudo retirar el carrito abandonado:", error.code || error.message));
      }
      return;
    }

    abandonedTimer.current = window.setTimeout(() => {
      const payload = abandonedPayload(formRef.current);
      if (!payload) return;
      const signature = JSON.stringify([
        payload.nombre,
        payload.telefono,
        payload.productoId,
        payload.ofertaCantidad,
        payload.ofertaPrecio,
      ]);
      if (signature === lastAbandonedSignature.current) return;
      queueAbandonedOperation(() => guardarCarritoAbandonado(payload))
        .then(() => { lastAbandonedSignature.current = signature; })
        .catch((error) => console.warn("No se pudo registrar el carrito abandonado:", error.code || error.message));
    }, 900);
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const nombre = String(form.get("nombre") || "").trim();
    const telefono = String(form.get("telefono") || "").replace(/\D/g, "");
    const ciudad = String(form.get("ciudad") || "");
    const direccion = String(form.get("direccion") || "").trim();
    const referencia = String(form.get("referencia") || "").trim();
    const metaConsent = buildMetaConsentFields(form.get("consentimientoMedicionMeta"));
    if (nombre.length < 5 || !/^09\d{8}$/.test(telefono) || !province || !ciudad || direccion.length < 25 || referencia.length < 15) return;

    if (!pendingOrderAttempt.current) {
      pendingOrderAttempt.current = { requestId: newRequestId() };
    }
    const attempt = pendingOrderAttempt.current;
    submitting.current = true;
    setSending(true);
    try {
      const saved = await crearPedidoLanding({
        ...attempt,
        landingSlug: slug,
        offerQuantity: offer.qty,
        cliente: {
          name: nombre,
          surname: "",
          phone: telefono,
          provincia: province,
          ciudad,
          address: direccion,
          notes: `Referencia: ${referencia}${form.get("observaciones") ? ` · ${String(form.get("observaciones"))}` : ""}`,
        },
        ...metaConsent,
        attribution: metaConsent.consentimientoMeta ? collectCampaignAttribution() : null,
      });
      if (abandonedTimer.current) window.clearTimeout(abandonedTimer.current);
      const cartPayload = abandonedPayload(formElement);
      if (cartPayload) {
        try {
          await abandonedWrite.current.catch(() => {});
          await marcarCarritoConvertido(cartPayload);
          clearAbandonedCartVisitorId(slug);
          abandonedVisitorId.current = "";
          lastAbandonedSignature.current = "";
        } catch (cartError) {
          console.warn("El pedido se guardó, pero no se pudo convertir el carrito:", cartError.code || cartError.message);
        }
      }
      if (metaConsent.consentimientoMeta) {
        sendPixel("Lead", {
          content_ids: [saved.productId],
          content_type: "product",
          value: saved.total,
          currency: "USD",
          num_items: saved.quantity,
        }, saved.eventId);
      }
      pendingOrderAttempt.current = null;
      setOrderId(saved.id);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      submitting.current = false;
      alert("No pudimos registrar el pedido. Revisa tu conexión e inténtalo nuevamente.");
    } finally {
      setSending(false);
    }
  };
  if (orderId) { const message = encodeURIComponent("Hola ROMI STORE, acabo de hacer un pedido."); return <main className="campaign-thanks"><div className="campaign-check">✓</div><span>PEDIDO REGISTRADO</span><h1>¡Gracias por tu compra!</h1><p>Tu pedido <b>#{orderId.slice(0, 8).toUpperCase()}</b> fue recibido correctamente.</p><a href={`https://wa.me/${STORE_WHATSAPP}?text=${message}`} target="_blank" rel="noreferrer">AVISAR POR WHATSAPP<small>Mensaje: “Hola ROMI STORE, acabo de hacer un pedido”</small></a><em>El mensaje no se envía automáticamente. En WhatsApp pulsa Enviar.</em></main>; }
  const marquee = landing.marquee || ["ENVÍO GRATIS A TODO ECUADOR", "PAGO CONTRA ENTREGA", "COMPRA SEGURA"];
  const renderOrder = (formBlock = {}) => {
    const formText = { ...DEFAULT_FORM_TEXT, ...(formBlock.formText || {}) };
    const productImage = formBlock.formProductImage || landing.images[0];
    const formStyle = {
      background: formBlock.background || landing.editor?.formBackground || theme.form,
      color: formBlock.color || landing.editor?.textColor || theme.formInk,
      "--campaign-label-size": `${formBlock.formLabelFontSize || 14}px`,
      "--campaign-input-size": `${formBlock.formInputFontSize || 14}px`,
    };
    const submitStyle = {
      background: formBlock.buttonBackground || landing.editor?.buttonBackground || theme.accent,
      color: formBlock.buttonColor || landing.editor?.buttonColor || theme.accentInk,
      borderRadius: formBlock.buttonRadius ?? 12,
      fontFamily: formBlock.buttonFontFamily || "Poppins, Arial, sans-serif",
      fontSize: formBlock.buttonFontSize || 18,
      fontWeight: formBlock.buttonFontWeight || 900,
      fontStyle: formBlock.buttonFontStyle || "normal",
    };
    return <section id="pedido" className="campaign-order" style={formStyle} onFocus={startCheckout}><header><span>COMPRA SEGURA</span><h2>{formBlock.text || "Completa tus datos para recibir tu pedido"}</h2><p>Pagas al recibir en la comodidad de tu hogar.</p></header><form ref={formRef} onChange={scheduleAbandonedCart} onSubmit={submit}>
      <fieldset><legend>1. {formText.offerTitle}</legend><div className="campaign-offers">{landing.offers.map((item) => <label className={offer.qty === item.qty ? "selected" : ""} key={item.qty}><input type="radio" name="oferta" checked={offer.qty === item.qty} onChange={() => setSelectedOfferQty(item.qty)} /><img src={productImage} alt="Producto" /><span><small>{item.featured ? "MEJOR OPCIÓN" : "PRECIO PROMOCIONAL"}</small><b>{item.qty} unidad{item.qty > 1 ? "es" : ""}</b></span><strong>${item.price.toFixed(2)}</strong></label>)}</div></fieldset>
      <fieldset><legend>2. {formText.shippingTitle}</legend><div className="campaign-fields"><label>{formText.nameLabel}<input name="nombre" required minLength="5" placeholder={formText.namePlaceholder} /></label><label>{formText.phoneLabel}<input name="telefono" required inputMode="numeric" pattern="09[0-9]{8}" maxLength="10" placeholder={formText.phonePlaceholder} /></label><label>{formText.provinceLabel}<select required value={province} onChange={(event) => setProvince(event.target.value)}><option value="">Selecciona provincia</option>{ECUADOR.map((item) => <option key={item.provincia}>{item.provincia}</option>)}</select></label><label>{formText.cityLabel}<select name="ciudad" required disabled={!province}><option value="">Selecciona ciudad</option>{cities.map((city) => <option key={city}>{city}</option>)}</select></label><label className="wide">{formText.addressLabel}<textarea name="direccion" required minLength="25" rows="3" placeholder={formText.addressPlaceholder} /><small>Escribe las calles completas. Una dirección incompleta puede impedir la entrega.</small></label><label className="wide">{formText.referenceLabel}<textarea name="referencia" required minLength="15" rows="3" placeholder={formText.referencePlaceholder} /></label><label className="wide">{formText.observationsLabel}<textarea name="observaciones" rows="2" placeholder={formText.observationsPlaceholder} /></label></div></fieldset>
      <fieldset><legend>3. {formText.summaryTitle}</legend><div className="campaign-summary"><img src={productImage} alt="Producto" /><span><b>{landing.product}</b><small>{offer.qty} unidad{offer.qty > 1 ? "es" : ""}</small></span><strong>${offer.price.toFixed(2)}</strong></div><p className="campaign-total"><span>Total a pagar al recibir</span><b>${offer.price.toFixed(2)}</b></p></fieldset>
      <label className="campaign-consent"><input type="checkbox" required /> <span>{formText.consentText}</span></label>
      <label className="campaign-consent campaign-consent--followup"><input type="checkbox" name="seguimientoWhatsapp" /> <span><b>Seguimiento opcional:</b> si no termino este pedido, autorizo a ROMI STORE EC a contactarme por WhatsApp para ayudarme a completar esta oferta. Puedo retirar esta autorización. <a href="/politicas-privacidad" target="_blank" rel="noreferrer">Ver política de privacidad</a>.</span></label>
      <label className="campaign-consent campaign-consent--meta"><input type="checkbox" name="consentimientoMedicionMeta" value="si" /> <span><b>Ayúdanos a medir y mejorar nuestros anuncios (opcional)</b> Al marcar, autorizas a ROMI STORE EC a compartir con Meta datos de contacto protegidos y datos básicos del pedido para medir resultados. Nunca compartimos tu dirección. <a href="/politicas-privacidad" target="_blank" rel="noreferrer">Más información</a>.</span></label><button className={`campaign-submit campaign-effect-${formBlock.buttonEffect || "pulse"}`} style={submitStyle} disabled={sending}>{sending ? "REGISTRANDO PEDIDO..." : (formBlock.buttonText || "CONFIRMAR MI PEDIDO")}<small>Pago seguro contra entrega</small></button>
    </form></section>;
  };
  if (landing.editor) {
    const editorStyle = { ...pageStyle, "--campaign-editor-width": `${landing.editor.contentWidth}px`, background: landing.editor.pageBackground, color: landing.editor.textColor };
    return <main className="campaign-page campaign-page--editor" style={editorStyle}>
      <span id="snap-ready" hidden />
      <div className="campaign-editor-content">{landing.editor.blocks.map((block) => <CampaignEditorBlock key={block.id} block={block} config={landing.editor} landing={landing} goToForm={goToForm} renderOrder={renderOrder} />)}</div>
    </main>;
  }
  return <main className="campaign-page" style={pageStyle}>
    <span id="snap-ready" hidden />
    <div className="campaign-marquee"><div>{[...marquee, ...marquee].map((text, index) => <b key={`${text}-${index}`}>{text}</b>)}</div></div>
    <section className="campaign-title"><h1>{landing.product}</h1><p>{landing.subtitle}</p></section>
    {landing.images.map((image, index) => <React.Fragment key={image}><section className="campaign-image"><img src={image} alt={`${landing.product} ${index + 1}`} /></section><button className="campaign-cta pulse" onClick={goToForm}>{landing.ctas?.[index] || "QUIERO HACER MI PEDIDO"}</button></React.Fragment>)}
    {landing.extraImage && <section className="campaign-image"><img src={landing.extraImage} alt="Envíos y pago contra entrega" /></section>}
    {renderOrder()}
  </main>;
}
